import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { Sort, SortDirection } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { addDays, format } from 'date-fns';

import { PaginationParams } from '@app/models';
import { AnalyticService } from '@app/services/analytic.service';
import { DriverService } from '@app/services/driver.service';
import { InterventionService } from '@app/services/intervention.service';
import { PermissionService } from '@app/services/permission.service';
import { convertDateFormat } from '@app/shared/helpers/date.helper';
import { toTitleCase } from '@app/shared/helpers/string.helper';
import { AuthService } from '@app/services/auth.service';
import { CoachingStatusType } from '@app/models/status.model';
import { Subscription } from 'rxjs';
import { OrganizationService } from '@app/services/organization.service';
import { MediaService } from '@app/services/media.service';
import { MatDialog } from '@angular/material/dialog';
import { FlyoutService } from '@app/services/flyout.service';
import { FlyoutType } from '@app/models/fly-out.model';
import { DialogCoachingDetailsComponent } from '@app/shared/components/dialog-coaching-details/dialog-coaching-details.component';
import { calculateDialogWidth } from '@app/shared/helpers/functions.helper';
import { MOBILE_WIDTH } from '@app/shared/config.constants';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
})
export class HomeComponent implements OnInit, OnDestroy, AfterViewInit {
  loginDisplay = false;
  isLoadingAtAGlanceMetrics: boolean = true;
  metrics: any;

  isLoadingMyDriversList: boolean = true;
  showNoDataAvailableMyDrivers: boolean = false;

  displayedColumnsMyDrivers: string[] = [];

  private columnsWithScore: string[] = [
    'driverName',
    'terminal',
    'driverScore',
    'scoreChangeType',
  ];

  private columnsWithoutScore: string[] = [
    'driverName',
    'terminal',
    'driverId',
    'hireDate',
  ];

  myDriversList!: MatTableDataSource<any>;
  defaultSortDirectionMyDrivers: SortDirection = 'desc';
  defaultSortActiveMyDrivers = 'driverScore';
  pageEventMyDrivers!: PageEvent;
  paginationParamsMyDrivers!: PaginationParams;
  sortMyDrivers!: Sort;

  isLoadingMyIncompleteInterventionsList: boolean = true;
  showNoDataAvailableMyIncompleteInterventions: boolean = false;
  displayedColumnsMyIncompleteInterventions: string[] = [
    'key',
    'driverID',
    'reason',
    'dueDate',
  ];
  myIncompleteInterventionsList!: MatTableDataSource<any>;
  defaultSortDirectionMyIncompleteInterventions: SortDirection = 'desc';
  defaultSortActiveMyIncompleteInterventions = 'dueDate';
  pageEventMyIncompleteInterventions!: PageEvent;
  paginationParamsMyIncompleteInterventions!: PaginationParams;
  sortMyIncompleteInterventions!: Sort;

  defaultPageSize: number = 10;
  pageSizeOptions: number[] = [10];

  selectedTimePeriod: string = '7';

  showFullSizeTable: boolean = false;
  showWelcomeMessage: boolean = false;

  selectedOrganization: any = {};

  private subscriptions = new Subscription();

  toTitleCase = toTitleCase;
  convertDateFormat = convertDateFormat;

  @ViewChild('slider') slider!: ElementRef;

  // Negative Score Changes properties
  isLoadingNegativeScoreChanges: boolean = true;
  showNoDataAvailableNegativeScoreChanges: boolean = false;
  displayedColumnsNegativeScoreChanges: string[] = [
    'driverName',
    'driverId',
    'ufId',
    'eventTypes',
    'daysSinceIntervention',
    'scoreBeforeIntervention',
    'scoreChange',
    'scoreAfterIntervention',
    'completedDate',
  ];
  negativeScoreChangesList!: MatTableDataSource<any>;
  defaultSortDirectionNegativeScoreChanges: SortDirection = 'desc';
  defaultSortActiveNegativeScoreChanges = 'completedDate';
  pageEventNegativeScoreChanges!: PageEvent;
  paginationParamsNegativeScoreChanges!: PaginationParams;
  sortNegativeScoreChanges!: Sort;
  selectedNegativeScorePeriod: string = '30'; // Default to 30 days

  constructor(
    private analyticService: AnalyticService,
    private driverService: DriverService,
    private interventionService: InterventionService,
    private permissionService: PermissionService,
    private authService: AuthService,
    private router: Router,
    private organizationService: OrganizationService,
    private mediaService: MediaService,
    private dialog: MatDialog,
    private flyoutService: FlyoutService
  ) {
    this.myDriversList = new MatTableDataSource<any>([]);
    this.myIncompleteInterventionsList = new MatTableDataSource<any>([]);
    this.negativeScoreChangesList = new MatTableDataSource<any>([]);
  }

  async ngOnInit(): Promise<void> {
    await this.loadSelectedOrganization();
    this.authService.getB2cObjectId().subscribe({
      next: (objectId) => {
        if (objectId) {
          this.permissionService.loadUserData(objectId).subscribe({
            next: () => {
              this.subscriptionToOrganizationChange();
              this.checkPermissionsAndWelcomeMessage();
              this.setPaginatorConfig();
              this.setShowFullSizeTable();
              this.setupDisplayedColumnsMyDrivers();
              this.fetchAtAGlanceMetrics();
              this.fetchMyDriversList();
              this.fetchMyIncompleteInterventionsList();
              this.fetchNegativeScoreChangesList();
            },
            error: (err) => {
              console.error('Error loading user data', err);
            },
          });
        }
      },
      error: (err) => {
        console.error('Error retrieving object ID', err);
      },
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private setupDisplayedColumnsMyDrivers(): void {
    if (this.canViewDriverScore()) {
      this.displayedColumnsMyDrivers = this.columnsWithScore;
    } else {
      this.displayedColumnsMyDrivers = this.columnsWithoutScore;
      this.defaultSortActiveMyDrivers = 'driverId';
    }
  }

  getFormattedHireDate(hireDateString: string): string {
    if (!hireDateString) return '';
    const year = hireDateString.substring(0, 4);
    const month = hireDateString.substring(5, 7);
    const day = hireDateString.substring(8, 10);
    return `${month}/${day}/${year}`;
  }

  subscriptionToOrganizationChange(): void {
    this.subscriptions.add(
      this.organizationService.organization$.subscribe((organization) => {
        if (organization) {
          this.selectedOrganization = organization;
          this.fetchAtAGlanceMetrics();
          this.fetchMyDriversList();
          this.fetchMyIncompleteInterventionsList();
        }
      })
    );
  }

  async loadSelectedOrganization(): Promise<void> {
    let retries = 3;

    while (retries > 0) {
      const storedOrganization = localStorage.getItem('selectedOrganization');

      if (storedOrganization) {
        this.selectedOrganization = JSON.parse(storedOrganization);
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, 1000));
      retries--;
    }
  }

  checkPermissionsAndWelcomeMessage(): void {
    if (
      !this.hasDriverProfilePermissions() &&
      !this.canViewDriverScore() &&
      !this.canViewTotalEvents() &&
      !this.hasInterventionsPermissions()
    ) {
      this.showWelcomeMessage = true;
    }
  }

  setShowFullSizeTable(): void {
    this.showFullSizeTable = !(
      this.hasInterventionsPermissions() && this.hasDriverProfilePermissions()
    );
  }

  ngAfterViewInit() {
    this.updateSliderPosition();
    this.updateNegativeSliderPosition();
  }

  onTimePeriodChange(period: string): void {
    this.selectedTimePeriod = period;
    this.updateSliderPosition();
    this.fetchAtAGlanceMetrics();
  }

  updateSliderPosition() {
    const mainSliderContainer = document.querySelector('.main-slider');
    if (!mainSliderContainer) return;

    const labels = mainSliderContainer.querySelectorAll('.new-radio-label');
    labels.forEach((label) => {
      label.classList.remove('selected');
    });

    const activeLabel = Array.from(labels).find(
      (label) =>
        label.getAttribute('for') ===
        `option${
          this.selectedTimePeriod.charAt(0).toUpperCase() +
          this.selectedTimePeriod.slice(1)
        }`
    ) as HTMLElement;

    if (activeLabel) {
      activeLabel.classList.add('selected');
    }

    if (this.slider && this.slider.nativeElement && activeLabel) {
      if (this.selectedTimePeriod === '7') {
        this.slider.nativeElement.style.left = `${activeLabel.offsetLeft}px`;
        this.slider.nativeElement.style.width = `70px`;
      } else if (this.selectedTimePeriod === '30') {
        this.slider.nativeElement.style.left = `${activeLabel.offsetLeft}px`;
        this.slider.nativeElement.style.width = `80px`;
      } else if (this.selectedTimePeriod === '90') {
        this.slider.nativeElement.style.left = `${activeLabel.offsetLeft}px`;
        this.slider.nativeElement.style.width = `85px`;
      }
    }
  }

  updateNegativeSliderPosition() {
    const negativeSliderContainer = document.querySelector('.negative-slider-container');
    if (!negativeSliderContainer) return;

    const labels = negativeSliderContainer.querySelectorAll('.new-radio-label');
    labels.forEach((label) => {
      label.classList.remove('selected');
    });

    const activeLabel = Array.from(labels).find(
      (label) =>
        label.getAttribute('for') === `negativeOption${this.selectedNegativeScorePeriod}`
    ) as HTMLElement;

    if (activeLabel) {
      activeLabel.classList.add('selected');
    }

    // Find the slider within the specific container
    const sliderElement = negativeSliderContainer.querySelector('.slider') as HTMLElement;

    if (sliderElement && activeLabel) {
      if (this.selectedNegativeScorePeriod === '7') {
        sliderElement.style.left = `${activeLabel.offsetLeft}px`;
        sliderElement.style.width = `70px`;
      } else if (this.selectedNegativeScorePeriod === '30') {
        sliderElement.style.left = `${activeLabel.offsetLeft}px`;
        sliderElement.style.width = `80px`;
      } else if (this.selectedNegativeScorePeriod === '90') {
        sliderElement.style.left = `${activeLabel.offsetLeft}px`;
        sliderElement.style.width = `85px`;
      }
    }
  }

  setPaginatorConfig(): void {
    this.paginationParamsMyDrivers = {
      limit: this.defaultPageSize,
      offset: 0,
      total: 0,
    };
    this.pageEventMyDrivers = {
      length: this.paginationParamsMyDrivers.total || 0,
      pageIndex: 0,
      pageSize: this.paginationParamsMyDrivers.limit,
    };

    this.paginationParamsMyIncompleteInterventions = {
      limit: this.defaultPageSize,
      offset: 0,
      total: 0,
    };
    this.pageEventMyIncompleteInterventions = {
      length: this.paginationParamsMyIncompleteInterventions.total || 0,
      pageIndex: 0,
      pageSize: this.paginationParamsMyIncompleteInterventions.limit,
    };

    this.paginationParamsNegativeScoreChanges = {
      limit: this.defaultPageSize,
      offset: 0,
      total: 0,
    };
    this.pageEventNegativeScoreChanges = {
      length: this.paginationParamsNegativeScoreChanges.total || 0,
      pageIndex: 0,
      pageSize: this.paginationParamsNegativeScoreChanges.limit,
    };
  }

  fetchAtAGlanceMetrics(): void {
    this.isLoadingAtAGlanceMetrics = true;
    const request = {
      lookBackPeriod: this.selectedTimePeriod,
      organizationId: this.selectedOrganization.organizationId,
    };
    this.analyticService.getAtAGlanceMetrics(request).subscribe({
      next: (res) => {
        this.metrics = res;
        this.isLoadingAtAGlanceMetrics = false;
      },
      error: (error) => {
        this.isLoadingAtAGlanceMetrics = false;
        console.log(error);
      },
    });
  }

  announceSortChangeMyDrivers(sort: Sort): void {
    this.sortMyDrivers = sort;
    this.fetchMyDriversList();
  }

  announceSortChangeMyIncompleteInterventions(sort: Sort): void {
    this.sortMyIncompleteInterventions = sort;
    this.fetchMyIncompleteInterventionsList();
  }

  fetchMyDriversList(): void {
    this.isLoadingMyDriversList = true;
    const userId = localStorage.getItem('objectId');
    const params = {
      page: this.pageEventMyDrivers.pageIndex + 1,
      pageSize: this.pageEventMyDrivers.pageSize,
      sortField: this.sortMyDrivers
        ? this.sortMyDrivers.active
        : this.defaultSortActiveMyDrivers,
      sortValue: this.sortMyDrivers
        ? this.sortMyDrivers.direction.toUpperCase()
        : this.defaultSortDirectionMyDrivers.toUpperCase(),
      bookMarkOnly: true,
      userId,
      organizationId: this.selectedOrganization.organizationId,
    };
    this.driverService.getDriverList(params).subscribe({
      next: (res: any) => {
        if (res.length) {
          this.paginationParamsMyDrivers.total = res[0].totalRecordCount;
          this.myDriversList.data = res[0].driverSummaries;
          if (!this.paginationParamsMyDrivers.total) {
            this.showNoDataAvailableMyDrivers = true;
          }
        } else {
          this.paginationParamsMyDrivers.total = 0;
          this.showNoDataAvailableMyDrivers = true;
          this.myDriversList.data = [];
        }
        this.isLoadingMyDriversList = false;
      },
      error: (error) => {
        this.isLoadingMyDriversList = false;
        console.log(error);
      },
    });
  }

  fetchMyIncompleteInterventionsList() {
    this.isLoadingMyIncompleteInterventionsList = true;
    const userId = localStorage.getItem('objectId');
    const params = {
      pageNumber: this.pageEventMyIncompleteInterventions.pageIndex + 1,
      pageSize: this.pageEventMyIncompleteInterventions.pageSize,
      sortField: this.sortMyIncompleteInterventions
        ? this.sortMyIncompleteInterventions.active
        : this.defaultSortActiveMyIncompleteInterventions,
      sortValue: this.sortMyIncompleteInterventions
        ? this.sortMyIncompleteInterventions.direction.toUpperCase()
        : this.defaultSortDirectionMyIncompleteInterventions.toUpperCase(),
      assignee: userId,
      status: `${CoachingStatusType.OPEN},${CoachingStatusType.SCHEDULED},${CoachingStatusType.IN_PROGRESS}`,
      organizationId: this.selectedOrganization.organizationId,
    };
    this.interventionService.getInterventionsList(params).subscribe({
      next: (res) => {
        if (res.length) {
          this.myIncompleteInterventionsList.data = res[0].AllInterventions.slice();
          this.paginationParamsMyIncompleteInterventions.total = res[0].totalRecordCount;
          if (!this.paginationParamsMyIncompleteInterventions.total) {
            this.showNoDataAvailableMyIncompleteInterventions = true;
          }
        } else {
          this.paginationParamsMyIncompleteInterventions.total = 0;
          this.myIncompleteInterventionsList.data = [];
          this.showNoDataAvailableMyIncompleteInterventions = true;
        }
        this.isLoadingMyIncompleteInterventionsList = false;
      },
      error: (error) => {
        this.isLoadingMyIncompleteInterventionsList = false;
        console.log(error);
      },
    });
  }

  onPageChangeMyDrivers(event: PageEvent): void {
    this.pageEventMyDrivers = event;
    this.fetchMyDriversList();
  }

  onPageChangeMyIncompleteInterventions(event: PageEvent): void {
    this.pageEventMyIncompleteInterventions = event;
    this.fetchMyIncompleteInterventionsList();
  }

  getImageUrl(driverId: string): string {
    return this.mediaService.getDriverImageURL(driverId);
  }

  isScoreBlockVisible(score: number): boolean {
    return !isNaN(score);
  }

  getSymbol(scoreChangeType: string): string {
    if (scoreChangeType === 'Positive') {
      return '+';
    }
    return '';
  }

  hasDriverProfilePermissions(): boolean {
    return (
      this.permissionService.hasPermission('READ', 'Driver Profile') ||
      this.permissionService.hasPermission('WRITE', 'Driver Profile')
    );
  }

  canViewDriverScore(): boolean {
    return (
      this.permissionService.hasPermission('READ', 'Driver Scores') ||
      this.permissionService.hasPermission('WRITE', 'Driver Scores')
    );
  }

  canViewTotalEvents(): boolean {
    return (
      this.permissionService.hasPermission('READ', 'Events') ||
      this.permissionService.hasPermission('WRITE', 'Events')
    );
  }

  hasInterventionsPermissions(): boolean {
    return (
      this.permissionService.hasPermission('READ', 'All Interventions') ||
      this.permissionService.hasPermission('WRITE', 'All Interventions') ||
      this.permissionService.hasPermission('READ', 'My Interventions') ||
      this.permissionService.hasPermission('WRITE', 'My Interventions')
    );
  }

  goToPage(route: string): void {
    this.router.navigate([route]);
  }

  goToPageWithTab(route: string, tab: string): void {
    this.router.navigate([route], { queryParams: { tab } });
  }

  goToPageWithDateRange(route: string, period: string): void {
    const endDate = new Date();
    let startDate = new Date();

    switch (period) {
      case '7':
        startDate = addDays(endDate, -7);
        break;
      case '30':
        startDate = addDays(endDate, -30);
        break;
      case '90':
        startDate = addDays(endDate, -90);
        break;
    }

    this.router.navigate([route], {
      queryParams: {
        startDate: format(startDate, 'M-dd-yyyy'),
        endDate: format(endDate, 'M-dd-yyyy'),
      },
    });
  }

  goToPageWithView(route: string, view: string): void {
    this.router.navigate([route], { queryParams: { view } });
  }

  onDriverRowClick(data: any): void {
    this.router.navigate(['/drivers'], { queryParams: { driverId: data.id } });
  }

  onInterventionRowClick(data: any): void {
    this.router.navigate(['/intervention/coaching'], {
      queryParams: { view: 'list', interventionId: data.id, driverGuid: data.driverGuid },
    });
  }

  onNegativeScorePeriodChange(period: string): void {
    this.selectedNegativeScorePeriod = period;
    this.updateNegativeSliderPosition();
    this.fetchNegativeScoreChangesList();
  }

  announceSortChangeNegativeScoreChanges(sort: Sort): void {
    this.sortNegativeScoreChanges = sort;
    this.fetchNegativeScoreChangesList();
  }

  onPageChangeNegativeScoreChanges(event: PageEvent): void {
    this.pageEventNegativeScoreChanges = event;
    this.fetchNegativeScoreChangesList();
  }

  fetchNegativeScoreChangesList(): void {
    if (!this.canManageInterventions()) {
      return;
    }

    this.isLoadingNegativeScoreChanges = true;
    const userId = localStorage.getItem('objectId');

    // Calculate start and end dates based on selected period
    const endDate = new Date();
    let startDate = new Date();

    switch (this.selectedNegativeScorePeriod) {
      case '7':
        startDate = addDays(endDate, -7);
        break;
      case '30':
        startDate = addDays(endDate, -30);
        break;
      case '90':
        startDate = addDays(endDate, -90);
        break;
    }

    const params: any = {
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'),
      pageNumber: this.pageEventNegativeScoreChanges.pageIndex + 1,
      pageSize: this.pageEventNegativeScoreChanges.pageSize,
      evaluationDays: 90,
      assignee: userId,
      organizationId: this.selectedOrganization.organizationId,
      sortField: this.sortNegativeScoreChanges
        ? this.sortNegativeScoreChanges.active
        : this.defaultSortActiveNegativeScoreChanges,
      sortValue: this.sortNegativeScoreChanges
        ? this.sortNegativeScoreChanges.direction.toUpperCase()
        : this.defaultSortDirectionNegativeScoreChanges.toUpperCase(),
    };

    this.analyticService.getIntNegativeScoreChangesList(params).subscribe({
      next: (res) => {
        if (res.totalCount > 0) {
          this.negativeScoreChangesList.data = res.results;
          this.paginationParamsNegativeScoreChanges.total = res.totalCount;
          this.showNoDataAvailableNegativeScoreChanges = false;
        } else {
          this.negativeScoreChangesList.data = [];
          this.paginationParamsNegativeScoreChanges.total = 0;
          this.showNoDataAvailableNegativeScoreChanges = true;
        }
        this.isLoadingNegativeScoreChanges = false;
      },
      error: (error) => {
        this.isLoadingNegativeScoreChanges = false;
        console.log(error);
      },
    });
  }

  onNegativeScoreInterventionSelected(intervention: any): void {
    this.interventionService.getIntervention(intervention.id).subscribe({
      next: (res) => {
        const detailedIntervention = res[0];
        this.openInterventionDetail(detailedIntervention);
      },
      error: (error) => {
        console.log(error);
      },
    });
  }

  openInterventionDetail(intervention: any): void {
    this.flyoutService.handleDialogsOfType(FlyoutType.COACHING);
    const dialogWidth = calculateDialogWidth(
      this.flyoutService.getFlyoutOffsetBasedOnIndex()
    );

    const isMobile = window.innerWidth <= MOBILE_WIDTH;

    const config = isMobile
      ? {
          panelClass: 'full-screen-dialog',
          width: '100vw',
          height: '100vh',
          maxWidth: '100vw',
          maxHeight: '100vh',
          autoFocus: false,
          data: {
            key: intervention.ufId,
            ...intervention,
            uniqueIdentifier: FlyoutType.COACHING,
          },
        }
      : {
          data: {
            key: intervention.ufId,
            ...intervention,
            uniqueIdentifier: FlyoutType.COACHING,
          },
          position: {
            right: '0',
            top: '70px',
          },
          width: dialogWidth,
          minWidth: dialogWidth,
          panelClass: ['dialog-event', 'animate-slide-in-left'],
          autoFocus: false,
        };

    this.dialog
      .open(DialogCoachingDetailsComponent, config)
      .afterClosed()
      .subscribe(() => {});
  }

  canManageInterventions(): boolean {
    return (
      this.permissionService.hasPermission('WRITE', 'All Interventions') ||
      this.permissionService.hasPermission('WRITE', 'My Interventions')
    );
  }
}
